/**
 * Multicam speakers: who speaks when on a set, the people's names, and
 * batches of camera cuts (backend: multicamSpeakersService.ts and
 * multicamCutBatchService.ts).
 *
 * The Apollo client is loaded on first use, so screens that only show these
 * results don't pull it in with them.
 */

import { gql } from '@apollo/client';
import type { MulticamAnnotation } from '../graphqlService';
import { fetchMulticamAnnotations } from '../graphqlService';
import type { SpeakingActivity } from '../utils/speakerActivity';

const apollo = async () => (await import('../apolloClient')).client;

/** A refusal from the API, with its code (MULTICAM_LOCKED, MULTICAM_SPEAKING_STALE, ...). */
export class SpeakersApiError extends Error {
  constructor(message: string, readonly code: string | null) {
    super(message);
    this.name = 'SpeakersApiError';
  }
}

/** Codes whose messages are written for the creator. */
const READABLE_CODES = [
  'MULTICAM_VALIDATION_ERROR',
  'MULTICAM_LOCKED',
  'MULTICAM_SPEAKING_STALE',
  'MULTICAM_BATCH_FAILED',
  'MULTICAM_CONFLICT',
  'MULTICAM_NOT_FOUND',
  'MULTICAM_PERMISSION_DENIED',
  'FORBIDDEN',
];

const asApiError = (error: unknown): SpeakersApiError => {
  const raw = error as { message?: string; graphQLErrors?: any[]; networkError?: { result?: { errors?: any[] } } };
  const graphQLErrors: any[] = [...(raw?.graphQLErrors ?? []), ...(raw?.networkError?.result?.errors ?? [])];
  const first = graphQLErrors.find(Boolean);
  const code = first?.extensions?.code ? String(first.extensions.code) : null;
  return new SpeakersApiError(String(first?.message || raw?.message || 'The request failed.'), code);
};

/** In words for the creator: the API's own message when it wrote one for them. */
export const describeSpeakersError = (error: unknown, fallback: string): string => {
  if (error instanceof SpeakersApiError && error.code && READABLE_CODES.includes(error.code)) {
    if (error.code === 'MULTICAM_LOCKED') return 'This set was submitted for editing, so it can’t be changed.';
    return error.message;
  }
  const message = error instanceof Error ? error.message : '';
  if (/network|failed to fetch|load failed|timed? ?out|offline/i.test(message)) return `${fallback} Check your connection, then try again.`;
  return `${fallback} Try again in a moment.`;
};

const SPEAKING_ACTIVITY_FRAGMENT = gql`
  fragment MulticamSpeakingActivityFields on MulticamSpeakingActivity {
    version
    frameMs
    durationMs
    angleKeys
    confident
    reason
    runs
    basis
    computedAt
  }
`;

const CUT_FIELDS_FRAGMENT = gql`
  fragment MulticamBatchCutFields on MulticamAnnotation {
    id
    multicamSetId
    timelineTimeMs
    endTimeMs
    type
    targetAngleKey
    text
    category
    color
    createdAt
    updatedAt
  }
`;

export type SpeakingActivityWrite = Omit<SpeakingActivity, 'version' | 'computedAt'>;

export const saveSpeakingActivity = async (multicamSetId: string, activity: SpeakingActivityWrite): Promise<SpeakingActivity> => {
  try {
    const client = await apollo();
    const { data } = await client.mutate({
      mutation: gql`
        mutation SaveMulticamSpeakingActivity($multicamSetId: String!, $activity: MulticamSpeakingActivityInput!) {
          saveMulticamSpeakingActivity(multicamSetId: $multicamSetId, activity: $activity) {
            ...MulticamSpeakingActivityFields
          }
        }
        ${SPEAKING_ACTIVITY_FRAGMENT}
      `,
      variables: {
        multicamSetId,
        activity: {
          frameMs: activity.frameMs,
          durationMs: activity.durationMs,
          angleKeys: activity.angleKeys,
          confident: activity.confident,
          reason: activity.reason ?? null,
          runs: activity.runs,
          basis: activity.basis,
        },
      },
    });
    return data.saveMulticamSpeakingActivity;
  } catch (error) {
    throw asApiError(error);
  }
};

export interface SpeakerNameWrite {
  angleKey: string;
  speakerName: string;
}

/** Names the people on the set's angles; returns each angle's name as saved. */
export const setMulticamSpeakerNames = async (
  multicamSetId: string,
  names: SpeakerNameWrite[]
): Promise<Array<{ angleKey: string; speakerName: string | null }>> => {
  try {
    const client = await apollo();
    const { data } = await client.mutate({
      mutation: gql`
        mutation SetMulticamSpeakerNames($multicamSetId: String!, $names: [MulticamSpeakerNameInput!]!) {
          setMulticamSpeakerNames(multicamSetId: $multicamSetId, names: $names) {
            id
            updatedAt
            angles {
              angleKey
              speakerName
            }
          }
        }
      `,
      variables: { multicamSetId, names },
    });
    return data.setMulticamSpeakerNames.angles;
  } catch (error) {
    throw asApiError(error);
  }
};

export interface CutWrite {
  timelineTimeMs: number;
  endTimeMs?: number | null;
  type: 'SWITCH';
  targetAngleKey: string;
  text: string;
  category: string;
  color: string;
}

/** Most cuts the API takes in one batch, added or removed. */
export const MAX_CUT_BATCH = 2500;

/** Adds camera cuts and removes others, all or nothing. */
export const applyMulticamCuts = async (
  multicamSetId: string,
  add: CutWrite[],
  removeIds: string[]
): Promise<{ added: MulticamAnnotation[]; removedIds: string[] }> => {
  try {
    const client = await apollo();
    const { data } = await client.mutate({
      mutation: gql`
        mutation ApplyMulticamCuts($multicamSetId: String!, $add: [MulticamAnnotationInput!]!, $removeIds: [String!]) {
          applyMulticamCuts(multicamSetId: $multicamSetId, add: $add, removeIds: $removeIds) {
            added {
              ...MulticamBatchCutFields
            }
            removedIds
          }
        }
        ${CUT_FIELDS_FRAGMENT}
      `,
      variables: { multicamSetId, add, removeIds },
    });
    return data.applyMulticamCuts;
  } catch (error) {
    throw asApiError(error);
  }
};

/** Cuts and notes per page of the multicam annotations query. */
export const ANNOTATION_PAGE_SIZE = 500;

/**
 * The rest of a set's cuts and notes after a full first page: suggested cuts
 * can take a set past one page, and "replace all" must see every cut.
 */
export const fetchRestOfMulticamAnnotations = async (
  multicamSetId: string,
  firstPage: MulticamAnnotation[],
  pageSize = ANNOTATION_PAGE_SIZE
): Promise<MulticamAnnotation[]> => {
  const all: MulticamAnnotation[] = [];
  const seen: Record<string, true> = {};
  const add = (list: MulticamAnnotation[]) => {
    list.forEach((annotation) => {
      // Something added in another tab between pages shifts the next page: no doubles.
      if (!annotation || seen[annotation.id]) return;
      seen[annotation.id] = true;
      all.push(annotation);
    });
  };
  add(firstPage);
  // The API holds up to 10,000 per set.
  for (let page = 1; firstPage.length >= pageSize && page < 25; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await fetchMulticamAnnotations(multicamSetId, pageSize, page * pageSize);
    const list = Array.isArray(batch) ? batch : [];
    add(list);
    if (list.length < pageSize) break;
  }
  return all;
};
